import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../hooks/use-toast';
import { Globe, Link2, Mail, Copy, Trash2, Eye, EyeOff, Lock, Users, Check, Shield } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ClientPortalTab = ({ businessId, token, businessName, subscriptionStatus, isAdmin }) => {
  const [portalSettings, setPortalSettings] = useState(null);
  const [invites, setInvites] = useState([]);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  const storedToken = token || sessionStorage.getItem('mybookie_token');
  const authHeaders = storedToken ? { headers: { Authorization: `Bearer ${storedToken}` } } : {};
  const isAllowed = isAdmin || ['business', 'business_plus'].includes(subscriptionStatus);

  useEffect(() => {
    if (businessId && isAllowed) {
      fetchSettings();
      fetchInvites();
    }
  }, [businessId]);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/api/mybookie/client-portal/settings/${businessId}`, authHeaders);
      setPortalSettings(res.data);
    } catch (error) {
      console.error('Failed to load portal settings:', error);
    }
  };

  const fetchInvites = async () => {
    try {
      const res = await axios.get(`${API}/api/mybookie/client-portal/invites/${businessId}`, authHeaders);
      setInvites(res.data);
    } catch (error) {
      console.error('Failed to load invites:', error);
    }
  };

  const handleCreatePortal = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/mybookie/client-portal/create`, {
        business_id: businessId,
        password: password.trim() || ''
      }, authHeaders);
      setPortalSettings({ active: true, share_token: res.data.share_token, password_protected: res.data.password_protected });
      toast({ title: "Client Portal Created", description: "Your portal is now live. Share the link with clients." });
      setPassword('');
      fetchSettings();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to create portal", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleDisablePortal = async () => {
    try {
      await axios.delete(`${API}/api/mybookie/client-portal/disable/${businessId}`, authHeaders);
      setPortalSettings({ active: false });
      toast({ title: "Portal Disabled", description: "Clients can no longer access the portal." });
    } catch (error) {
      toast({ title: "Error", description: "Failed to disable portal", variant: "destructive" });
    }
  };

  const handleSendInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/api/mybookie/client-portal/invite`, {
        business_id: businessId,
        email: inviteEmail.trim(),
        name: inviteName.trim()
      }, authHeaders);
      toast({ title: "Invite Sent", description: `Magic link emailed to ${inviteEmail}` });
      setInviteEmail('');
      setInviteName('');
      fetchInvites();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Failed to send invite", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeInvite = async (inviteId) => {
    try {
      await axios.delete(`${API}/api/mybookie/client-portal/revoke-invite/${inviteId}`, authHeaders);
      toast({ title: "Invite Revoked" });
      fetchInvites();
    } catch (error) {
      toast({ title: "Error", description: "Failed to revoke invite", variant: "destructive" });
    }
  };

  const portalUrl = portalSettings?.share_token
    ? `${window.location.origin}/client-portal/${portalSettings.share_token}`
    : '';

  const copyLink = () => {
    navigator.clipboard.writeText(portalUrl);
    setCopied(true);
    toast({ title: "Link Copied!" });
    setTimeout(() => setCopied(false), 2000);
  };

  if (!isAllowed) {
    return (
      <Card data-testid="client-portal-upgrade">
        <CardContent className="p-8 text-center">
          <Lock className="w-12 h-12 text-[#8A948F] mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-[#111614] mb-2">Client Portal</h3>
          <p className="text-[#5C6661] mb-4">Share a read-only financial dashboard with your clients and investors. Available on Business and Business+ plans.</p>
          <Badge variant="outline">Upgrade to unlock</Badge>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="client-portal-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-[#111614]">Client Portal</h2>
          <p className="text-sm text-[#5C6661]">Share a read-only financial dashboard with clients and investors</p>
        </div>
        {portalSettings?.active && (
          <Badge className="bg-green-100 text-green-800">Portal Active</Badge>
        )}
      </div>

      {/* Portal Setup / Status */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Globe className="w-4 h-4" /> Portal Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!portalSettings?.active ? (
            <div className="space-y-4">
              <p className="text-sm text-[#5C6661]">Create a client portal for <strong>{businessName}</strong>. Clients will see a full financial snapshot including revenue, expenses, P&L, invoice status, and cash flow forecast.</p>
              <div>
                <Label>Password Protection (optional)</Label>
                <div className="relative mt-1">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Leave blank for no password"
                    data-testid="portal-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A948F]"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button onClick={handleCreatePortal} disabled={loading} data-testid="create-portal-btn" className="bg-[#0A2B1D] hover:bg-[#143D2B]">
                {loading ? 'Creating...' : 'Create Client Portal'}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label className="text-xs text-[#5C6661]">Portal Link</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={portalUrl} readOnly className="bg-[#F6F5F2] text-sm" data-testid="portal-link-input" />
                  <Button variant="outline" size="sm" onClick={copyLink} data-testid="copy-portal-link-btn">
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              <div className="flex items-center gap-4 text-sm text-[#5C6661]">
                {portalSettings.password_protected && (
                  <span className="flex items-center gap-1"><Lock className="w-3 h-3" /> Password protected</span>
                )}
                <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> Read-only access</span>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleCreatePortal} data-testid="update-portal-btn">
                  Update Settings
                </Button>
                <Button variant="destructive" size="sm" onClick={handleDisablePortal} data-testid="disable-portal-btn">
                  Disable Portal
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Email Invites */}
      {portalSettings?.active && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Mail className="w-4 h-4" /> Email Invites
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-[#5C6661]">Send a magic link — clients click once to access, no password needed.</p>
            <form onSubmit={handleSendInvite} className="flex flex-col sm:flex-row gap-2">
              <Input
                type="text"
                value={inviteName}
                onChange={(e) => setInviteName(e.target.value)}
                placeholder="Client name"
                className="flex-1"
                data-testid="invite-name-input"
              />
              <Input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="client@example.com"
                required
                className="flex-1"
                data-testid="invite-email-input"
              />
              <Button type="submit" disabled={loading} className="bg-[#0A2B1D] hover:bg-[#143D2B]" data-testid="send-invite-btn">
                <Mail className="w-4 h-4 mr-1" /> Send
              </Button>
            </form>

            {invites.length > 0 && (
              <div className="mt-4">
                <Label className="text-xs text-[#5C6661] mb-2 block">Sent Invites ({invites.length})</Label>
                <div className="space-y-2">
                  {invites.map((inv) => (
                    <div key={inv.id} className="flex items-center justify-between p-3 bg-[#F6F5F2] rounded-lg" data-testid={`invite-row-${inv.id}`}>
                      <div>
                        <span className="text-sm font-medium text-[#111614]">{inv.name || inv.email}</span>
                        {inv.name && <span className="text-xs text-[#5C6661] ml-2">{inv.email}</span>}
                        <div className="flex items-center gap-2 mt-0.5">
                          {inv.active ? (
                            <Badge className="bg-green-100 text-green-800 text-xs">Active</Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs">Revoked</Badge>
                          )}
                          {inv.last_accessed && (
                            <span className="text-xs text-[#8A948F]">Viewed {new Date(inv.last_accessed).toLocaleDateString()}</span>
                          )}
                        </div>
                      </div>
                      {inv.active && (
                        <Button variant="ghost" size="sm" onClick={() => handleRevokeInvite(inv.id)} data-testid={`revoke-invite-${inv.id}`}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Info */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Users className="w-5 h-5 text-[#0A2B1D] mt-0.5 flex-shrink-0" />
            <div className="text-sm text-[#5C6661]">
              <p className="font-medium text-[#111614] mb-1">What clients see</p>
              <ul className="space-y-1 list-disc list-inside">
                <li>Revenue & expense summaries with monthly trends</li>
                <li>P&L breakdown with profit margins</li>
                <li>Invoice status (paid, pending, overdue)</li>
                <li>Cash flow forecast (3-month projection)</li>
                <li>Expense breakdown by category</li>
              </ul>
              <p className="mt-2 text-xs">All data is read-only. Clients cannot modify any financial records.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ClientPortalTab;
